import { useEffect, useRef, useState } from "react";
import { Map as MLMap, Marker, NavigationControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api } from "@/lib/api";
import { MINERAL_COLORS } from "./MineralLayout";
import { Layers, Loader2, MapPin, AlertCircle } from "lucide-react";

const BRASIL_CENTER = [-52.0, -15.0];
const BRASIL_ZOOM = 3.4;

// Tile básico open-source (OSM standard) — sem key, uso não-comercial ok pra prototipagem
const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "&copy; OpenStreetMap contributors",
    },
  },
  layers: [{ id: "osm-base", type: "raster", source: "osm" }],
};

// Estilo escuro carto (sem chave)
const CARTO_DARK_STYLE = "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";

export default function MineralMap() {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [ready, setReady] = useState(false);
  const [estados, setEstados] = useState([]);
  const [selected, setSelected] = useState(null);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState("dark");

  useEffect(() => {
    if (map.current) return;
    map.current = new MLMap({
      container: mapContainer.current,
      style: style === "dark" ? CARTO_DARK_STYLE : OSM_STYLE,
      center: BRASIL_CENTER,
      zoom: BRASIL_ZOOM,
      attributionControl: true,
    });
    map.current.addControl(new NavigationControl({ showCompass: false }), "top-right");
    map.current.on("load", () => setReady(true));
    return () => { if (map.current) { map.current.remove(); map.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switch style
  useEffect(() => {
    if (!map.current || !ready) return;
    map.current.setStyle(style === "dark" ? CARTO_DARK_STYLE : OSM_STYLE);
  }, [style, ready]);

  // Load UFs
  useEffect(() => {
    api.get("/mineral/estados").then(r => setEstados(r.data.results || [])).catch(()=>{});
  }, []);

  const selectUf = async (uf) => {
    setSelected(uf);
    if (!uf) { setMunicipios([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/mineral/municipios/${uf.sigla}`);
      setMunicipios(r.data.results || []);
    } catch (e) {
      setMunicipios([]);
    } finally {
      setLoading(false);
    }
  };

  // Add UF marker layer when estados load
  useEffect(() => {
    if (!ready || !map.current || estados.length === 0) return;
    // Simple marker cluster: add a source with each UF centroide (aprox via nome — real virá via IBGE geojson)
    // Fase 2.5: sem geojson por enquanto, mas mostramos pins usando lat/lng conhecidas (centro UF).
    const UF_CENTROIDS = {
      AC:[-70.55,-8.77], AL:[-36.66,-9.71], AM:[-64.66,-3.47], AP:[-52.00,1.41], BA:[-38.30,-13.29],
      CE:[-38.54,-5.20], DF:[-47.86,-15.83], ES:[-40.31,-19.19], GO:[-49.86,-16.64], MA:[-45.24,-5.42],
      MG:[-44.55,-18.10], MS:[-54.85,-20.51], MT:[-55.42,-12.64], PA:[-52.29,-5.53], PB:[-36.72,-7.28],
      PE:[-37.07,-8.38], PI:[-42.28,-6.60], PR:[-51.61,-24.89], RJ:[-42.53,-22.25], RN:[-36.52,-5.81],
      RO:[-62.80,-10.83], RR:[-61.31,2.82], RS:[-53.05,-29.31], SC:[-49.44,-27.45], SE:[-37.07,-10.57],
      SP:[-48.63,-22.19], TO:[-48.33,-10.17],
    };
    for (const uf of estados) {
      const c = UF_CENTROIDS[uf.sigla];
      if (!c) continue;
      const el = document.createElement("div");
      el.className = "uf-marker";
      el.dataset.testid = `mineral-map-uf-${uf.sigla}`;
      el.style.cssText = `background:${MINERAL_COLORS.gold};color:${MINERAL_COLORS.navy};font:600 11px/1 -apple-system,sans-serif;border:2px solid ${MINERAL_COLORS.navy};border-radius:9999px;padding:5px 10px;cursor:pointer;box-shadow:0 3px 10px rgba(0,0,0,0.4);white-space:nowrap`;
      el.textContent = uf.sigla;
      el.addEventListener("click", () => {
        selectUf(uf);
        map.current.flyTo({ center: c, zoom: 6.5, duration: 1600 });
      });
      new Marker({ element: el, anchor: "center" }).setLngLat(c).addTo(map.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, estados]);

  return (
    <div className="space-y-4" data-testid="mineral-map-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: MINERAL_COLORS.goldDark }}>
            Fase 2.5 · MapLibre + IBGE
          </div>
          <h2 className="font-heading text-2xl font-semibold" style={{ color: MINERAL_COLORS.navy }}>
            <MapPin className="inline w-6 h-6 mr-2" style={{ color: MINERAL_COLORS.gold }}/>
            Mapa Minerário
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Base cartográfica ativa. Poligonais SIGMINE serão adicionadas assim que ANM estiver homologado.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setStyle(s => s === "dark" ? "osm" : "dark")}
            data-testid="mineral-map-style-toggle"
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50 inline-flex items-center gap-1">
            <Layers className="w-3 h-3"/> {style === "dark" ? "Escuro" : "OSM"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        {/* Lateral: UFs e municípios */}
        <aside className="bg-white rounded-xl border p-4 space-y-3 max-h-[560px] overflow-y-auto"
               style={{ borderColor: MINERAL_COLORS.divider }}>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Estados</div>
          <div className="grid grid-cols-3 gap-1">
            {estados.map(e => (
              <button key={e.sigla} onClick={() => selectUf(e)}
                data-testid={`mineral-map-list-uf-${e.sigla}`}
                className={`text-xs px-2 py-1.5 rounded transition ${
                  selected?.sigla === e.sigla
                    ? "bg-[#0B1B2F] text-[#D9BF82] font-medium"
                    : "hover:bg-slate-100 text-slate-600"
                }`}>
                {e.sigla}
              </button>
            ))}
          </div>
          {selected && (
            <div className="pt-2 border-t border-slate-100" data-testid="mineral-map-municipios">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">
                Municípios · {selected.nome}
              </div>
              {loading ? (
                <div className="text-xs text-slate-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin"/> Carregando IBGE…
                </div>
              ) : (
                <>
                  <div className="text-[11px] text-slate-500 mb-2">{municipios.length} municípios</div>
                  <div className="max-h-[220px] overflow-y-auto space-y-0.5 text-xs">
                    {municipios.slice(0, 200).map(m => (
                      <div key={m.id} className="px-2 py-1 rounded hover:bg-slate-50 truncate">
                        {m.nome}
                      </div>
                    ))}
                    {municipios.length > 200 && (
                      <div className="text-[10px] text-slate-400 pt-1">+ {municipios.length - 200} outros…</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </aside>

        {/* Mapa */}
        <div className="relative bg-white rounded-xl border overflow-hidden"
             style={{ borderColor: MINERAL_COLORS.divider, height: 560 }}>
          <div ref={mapContainer} className="absolute inset-0"/>
          {!ready && (
            <div className="absolute inset-0 grid place-items-center bg-slate-50">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400"/>
            </div>
          )}
        </div>
      </div>

      <div className="text-[11px] text-slate-500 flex items-start gap-2 bg-amber-50 border border-amber-200 p-3 rounded-lg">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-700"/>
        <div>
          Base cartográfica: MapLibre GL + CartoDB Dark Matter. Poligonais SIGMINE (processos minerários)
          serão adicionadas como layer PostGIS assim que a chave gov.br for configurada e a ingestão SCM+SIGMINE homologada.
        </div>
      </div>
    </div>
  );
}
